import React, { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, CardHeader, Button, Collapse, CardBody, FormGroup, Label, Input, FormFeedback } from 'reactstrap';
import BoolInput from '../../../../../misc/BoolInput';
import EnumInput from '../../../../../misc/EnumInput';
import { CoreGoogleGroupConfiguration, GoogleGroupGroupTypes, GoogleGroupMemberMode } from '../../../../../../models/CoreGoogleGroupModels';
import * as GoogleGroupTypes from './GoogleGroupTypes';
import NullableIntInputCheckboxText from '../../../../../misc/NullableIntInputCheckboxText';
import InformationMarkingsInput from '../InformationMarkingsInput';
import i18n from 'i18next';


export const validator = (configuration: CoreGoogleGroupConfiguration, active: boolean) => {
    const validation: string[] = [];

    if (active && configuration.groupTypes !== 0) {
        if (configuration.ownerMode !== GoogleGroupMemberMode.STRICT && configuration.ownerMode !== GoogleGroupMemberMode.PERMISSIVE) {
            validation.push('ownerMode')
        }
        if (configuration.memberMode !== GoogleGroupMemberMode.STRICT && configuration.memberMode !== GoogleGroupMemberMode.PERMISSIVE) {
            validation.push('memberMode')
        }
    }
    if (configuration.inactivePeriod < 0) {
        validation.push('inactivePeriod')
    }
    if (configuration.deletePeriod === undefined || (configuration.deletePeriod !== null && configuration.deletePeriod < 0)) {
        validation.push('deletePeriod');
    }

    return validation;
}

export const warningMessages = (originalConfiguration: CoreGoogleGroupConfiguration, updatedConfiguration: CoreGoogleGroupConfiguration) => {
    const warnings: string[] = [];

    var originalValue = originalConfiguration.groupTypes;
    var updateValue = updatedConfiguration.groupTypes
    //Find out if any grouptype is removed from update
    if (((originalValue ^ updateValue) & originalValue) !== 0) {
        warnings.push(i18n.t('googleGroupExport.warnings.groupType', { ns: 'configuration' }))
    }

    return warnings;
}


interface GoogleGroupExportConfigurationProps {
    configuration: CoreGoogleGroupConfiguration
    disabled: boolean
    updatePart: (part: Partial<CoreGoogleGroupConfiguration>) => void
    active: boolean
    onValidChange: (arg: boolean) => void 
}


const GoogleGroupExportConfiguration = (props: GoogleGroupExportConfigurationProps) => {
    const { t } = useTranslation(['configuration', 'common']);

    const [open, setOpen] = useState<boolean>(true);

    const validation = validator(props.configuration, props.active);

    useEffect(() => {
        props.onValidChange(validation.length ? true : false)
    }, [props.configuration, props.active]);

    return (
        <Card className="edit_module">
            <CardHeader tag="h2">
                <Button outline color="secondary" style={{ width: '37px', float: 'right' }} onClick={() => setOpen(!open)}>{open ? '-' : '+'}</Button>
                {t('modules.GoogleGroupExport.title', { ns: 'common' })}
            </CardHeader>
            <Collapse isOpen={open}>
                <CardBody>
                    <div style={{ margin: '1rem 0' }}>
                        <FormGroup>
                            <FormGroup>
                                <h5>{t('googleGroupExport.domainName.title', { ns: 'configuration' })}</h5>
                                <Label for="domainName"><Trans t={t} i18nKey={'googleGroupExport.domainName.label'} ns={'configuration'} components={{
                                    lnk: <Link to="https://admin.google.com/ac/domains/manage" target="blank" />
                                }} /></Label>
                                <Input
                                    className="md-w-50 lg-w-25"
                                    type="text"
                                    name="domainName"
                                    id="domainName"
                                    value={props.configuration.domainName || ''}
                                    onChange={(e) => props.updatePart({ domainName: e.target.value })}
                                    disabled={props.disabled}
                                />
                            </FormGroup>

                            <BoolInput
                                title={t('googleGroupExport.syncClassroomTeacherMembers', { ns: 'configuration' })}
                                name='syncClassroomTeacherMembers'
                                value={props.configuration.syncClassroomTeacherMembers}
                                updateValue={(name, value) => props.updatePart({ [name]: value })}
                                disabled={props.disabled}
                            />

                            <EnumInput
                                multi={true}
                                title={t('googleGroupExport.groupTypes.title', { ns: 'configuration' })}
                                name="groupTypes"
                                value={props.configuration.groupTypes}
                                options={GoogleGroupTypes.groupTypes.map(x => ({
                                    value: x,
                                    disabled: x === GoogleGroupGroupTypes.MentorGroup,
                                    text: t(`googleGroupExport.groupTypes.options.${x}`, { ns: 'configuration' })
                                }))}
                                disabled={props.disabled}
                                onChange={(name, value) => props.updatePart({ [name]: value })}
                                invalid={validation.indexOf('groupTypes') !== -1}
                                invalidFeedback={t('googleGroupExport.groupTypes.invalidFeedback', { ns: 'configuration' })}
                            />

                            <EnumInput
                                title={t('googleGroupExport.ownerMode.title', { ns: 'configuration' })}
                                name='ownerMode'
                                value={props.configuration.ownerMode}
                                options={GoogleGroupTypes.ownerMode.map(x => ({
                                    value: x,
                                    text: t(`googleGroupExport.ownerMode.options.${x}`, { ns: 'configuration' })
                                }))}
                                onChange={(name, value) => props.updatePart({ [name]: value })}
                                disabled={props.disabled}
                                invalid={validation.indexOf('ownerMode') !== -1}
                                invalidFeedback={t('googleGroupExport.ownerMode.invalidFeedback', { ns: 'configuration' })}
                            />

                            <EnumInput
                                title={t('googleGroupExport.memberMode.title', { ns: 'configuration' })}
                                name='memberMode'
                                value={props.configuration.memberMode}
                                options={GoogleGroupTypes.memberMode.map(x => ({
                                    value: x,
                                    text: t(`googleGroupExport.memberMode.options.${x}`, { ns: 'configuration' })
                                }))}
                                onChange={(name, value) => props.updatePart({ [name]: value })}
                                disabled={props.disabled}
                                invalid={validation.indexOf('memberMode') !== -1}
                                invalidFeedback={t('googleGroupExport.memberMode.invalidFeedback', { ns: 'configuration' })}
                            />

                            <EnumInput
                                title={t('googleGroupExport.whoCanViewMembership.title', { ns: 'configuration' })}
                                name='whoCanViewMembership'
                                value={props.configuration.whoCanViewMembership}
                                options={GoogleGroupTypes.whoCanViewMemberShip.map(x => ({
                                    value: x,
                                    text: t(`googleGroupExport.whoCanViewMembership.options.${x}`, { ns: 'configuration' })
                                }))}
                                onChange={(name, value) => props.updatePart({ [name]: value })}
                                disabled={props.disabled}
                                invalid={validation.indexOf('whoCanViewMembership') !== -1}
                                invalidFeedback={t('googleGroupExport.whoCanViewMembership.invalidFeedback', { ns: 'configuration' })}
                            />

                            <EnumInput
                                title={t('googleGroupExport.whoCanPostMessage.title', { ns: 'configuration' })}
                                name='whoCanPostMessage'
                                value={props.configuration.whoCanPostMessage}
                                options={GoogleGroupTypes.whoCanPostMessage.map(x => ({ value: x, text: t(`googleGroupExport.whoCanPostMessage.options.${x}`, { ns: 'configuration' }) }))}
                                onChange={(name, value) => props.updatePart({ [name]: value })}
                                disabled={props.disabled}
                                invalid={validation.indexOf('whoCanPostMessage') !== -1}
                                invalidFeedback={t('googleGroupExport.whoCanPostMessage.invalidFeedback', { ns: 'configuration' })}
                            />

                            <EnumInput
                                title={t('googleGroupExport.whoCanModerateMembers.title', { ns: 'configuration' })}
                                name='whoCanModerateMembers'
                                value={props.configuration.whoCanModerateMembers}
                                options={GoogleGroupTypes.whoCanModerateMembers.map(x => ({ value: x, text: t(`googleGroupExport.whoCanModerateMembers.options.${x}`, { ns: 'configuration' }) }))}
                                onChange={(name, value) => props.updatePart({ [name]: value })}
                                disabled={props.disabled}
                                invalid={validation.indexOf('whoCanModerateMembers') !== -1}
                                invalidFeedback={t('googleGroupExport.whoCanModerateMembers.invalidFeedback', { ns: 'configuration' })}
                            />

                            <EnumInput
                                title={t('googleGroupExport.whoCanJoin.title', { ns: 'configuration' })}
                                name='whoCanJoin'
                                value={props.configuration.whoCanJoin}
                                options={GoogleGroupTypes.whoCanJoin.map(x => ({ value: x, text: t(`googleGroupExport.whoCanJoin.options.${x}`, { ns: 'configuration' }) }))}
                                onChange={(name, value) => props.updatePart({ [name]: value })}
                                disabled={props.disabled}
                                invalid={validation.indexOf('whoCanJoin') !== -1}
                                invalidFeedback={t('googleGroupExport.whoCanJoin.invalidFeedback', { ns: 'configuration' })}
                            />

                            <EnumInput
                                title={t('googleGroupExport.whoCanViewGroup.title', { ns: 'configuration' })}
                                name='whoCanViewGroup'
                                value={props.configuration.whoCanViewGroup}
                                options={GoogleGroupTypes.whoCanViewGroup.map(x => ({ value: x, text: t(`googleGroupExport.whoCanViewGroup.options.${x}`, { ns: 'configuration' }) }))}
                                onChange={(name, value) => props.updatePart({ [name]: value })}
                                disabled={props.disabled}
                                invalid={validation.indexOf('whoCanViewGroup') !== -1}
                                invalidFeedback={t('googleGroupExport.whoCanViewGroup.invalidFeedback', { ns: 'configuration' })}
                            />

                            <BoolInput
                                title={t('googleGroupExport.includeInGlobalAddressList', { ns: 'configuration' })}
                                name='includeInGlobalAddressList'
                                value={props.configuration.includeInGlobalAddressList}
                                updateValue={(name, value) => props.updatePart({ [name]: value })}
                                disabled={props.disabled}
                            />

                            <FormGroup>
                                <h5>{t('googleGroupExport.lifeCycleManagement.title', { ns: 'configuration' })}</h5>
                                <FormGroup>
                                    <Label for="inactivePeriod">{t('googleGroupExport.lifeCycleManagement.archiveGracePeriod.title', { ns: 'configuration' })}</Label>
                                    <Input
                                        className="md-w-50 lg-w-25"
                                        type="number"
                                        min="0"
                                        step="1"
                                        pattern="[0-9]"
                                        name="inactivePeriod"
                                        id="inactivePeriod"
                                        value={props.configuration.inactivePeriod}
                                        onChange={(e) => props.updatePart({ inactivePeriod: Number(e.target.value) })}
                                        disabled={props.disabled}
                                        invalid={validation.indexOf('inactivePeriod') !== -1}
                                    />
                                    <FormFeedback>{t('googleGroupExport.lifeCycleManagement.archiveGracePeriod.invalidFeedback', { ns: 'configuration' })}</FormFeedback>
                                </FormGroup>

                                <NullableIntInputCheckboxText
                                    name="deletePeriod"
                                    value={props.configuration.deletePeriod}
                                    disabled={props.disabled}
                                    title={t('googleGroupExport.lifeCycleManagement.deleteGracePeriod.title', { ns: 'configuration' })}
                                    invalidFeedback={t('googleGroupExport.lifeCycleManagement.deleteGracePeriod.invalidFeedback', { ns: 'configuration' })}
                                    invalid={validation.indexOf('deletePeriod') !== -1}
                                    defaultValue={365}
                                    onChange={(name, value) => props.updatePart({ [name]: value })}
                                    checkboxTitle={t('googleGroupExport.lifeCycleManagement.deleteGracePeriod.checkbox', { ns: 'configuration' })}
                                />
                            </FormGroup>

                        </FormGroup>
                        <InformationMarkingsInput
                            selectedValues={props.configuration.activityInformationMarkingFilters}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            name='activityInformationMarkingFilters'
                        />
                    </div>
                </CardBody>
            </Collapse>
        </Card>)
}

export default GoogleGroupExportConfiguration;
